import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { UserPlus, Phone, KeyRound, Loader2, ChevronRight, Lock, RefreshCw, LogIn, Info, ChevronsUpDown, Check, Eye, EyeOff, MapPin } from 'lucide-react';
import { registerUser, verifyRegistration, userLogin, verifyLogin, checkReferralCode, resendCode } from '../lib/api';
import { useApp } from '../App';
import { toast } from 'sonner';
import mexicoLocations from '../data/mexico_locations.json';

const states = Object.keys(mexicoLocations).sort();

// Reusable searchable combobox
const SearchableSelect = ({ value, onSelect, options, placeholder, disabled, testId }) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={`flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm
            bg-[#121521] border-white/[0.12] text-[#F7F2E7] 
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#FFC640] cursor-pointer'}
            ${!value ? 'text-[#F7F2E7]/40' : ''}`}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 text-[#F7F2E7]/40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-[#121521] border-white/[0.12]" align="start">
        <Command className="bg-[#121521]">
          <CommandInput placeholder={`Buscar...`} className="text-[#F7F2E7] text-sm" />
          <CommandList className="max-h-[200px]">
            <CommandEmpty className="text-[#F7F2E7]/50 text-xs py-4">Sin resultados</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => { onSelect(opt); setOpen(false); }}
                  className="text-[#F7F2E7] text-sm cursor-pointer data-[selected=true]:bg-[rgba(255,198,64,0.1)] data-[selected=true]:text-[#FFC640]"
                >
                  <Check size={14} className={`mr-2 ${value === opt ? 'opacity-100 text-[#FFC640]' : 'opacity-0'}`} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const AuthGate = ({ onSuccess }) => {
  const { login } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('choice');
  const [form, setForm] = useState({
    name: '', phone: '', email: '', municipality: '', state: '', password: '', confirmPassword: '', referral_code: '',
  });
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [referralLocked, setReferralLocked] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [demoCode, setDemoCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const municipalities = useMemo(() => {
    if (!form.state || !mexicoLocations[form.state]) return [];
    return mexicoLocations[form.state].sort();
  }, [form.state]);

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setForm(f => ({ ...f, referral_code: refCode }));
      setReferralLocked(true);
      localStorage.setItem('cofres_referral_code', refCode);
      checkReferralCode(refCode).then(res => {
        if (res.data.valid) setReferralInfo(res.data);
        else { setReferralLocked(false); toast.error('El código de referido no es válido'); }
      }).catch(() => {});
    } else {
      const savedRef = localStorage.getItem('cofres_referral_code');
      if (savedRef) {
        setForm(f => ({ ...f, referral_code: savedRef }));
        setReferralLocked(true);
        checkReferralCode(savedRef).then(res => {
          if (res.data.valid) setReferralInfo(res.data);
          else setReferralLocked(false);
        }).catch(() => {});
      }
    }
  }, [searchParams]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Nombre requerido';
    if (!form.phone.trim() || form.phone.length < 10) e.phone = 'Teléfono inválido (mín. 10 dígitos)';
    if (!form.email.trim() || !form.email.includes('@')) e.email = 'Correo electrónico inválido';
    if (!form.state) e.state = 'Estado requerido';
    if (!form.municipality) e.municipality = 'Municipio requerido';
    if (!form.password || form.password.length < 6) e.password = 'Mínimo 6 caracteres';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Las contraseñas no coinciden';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const formatPhoneDisplay = (phone) => {
    let formatted = phone.trim();
    if (!formatted.startsWith('+')) {
      formatted = formatted.startsWith('52') ? '+' + formatted : '+52' + formatted;
    }
    return formatted;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { confirmPassword, ...payload } = form;
      const res = await registerUser(payload);
      if (res.data.success) {
        const match = res.data.message?.match(/Demo: (\d+)/);
        if (match) setDemoCode(match[1]);
        toast.success('Código de verificación enviado');
        setStep('verify');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al registrar';
      if (detail.includes('ya está registrado')) {
        toast.error(detail);
        setLoginPhone(form.phone);
        setStep('login');
      } else {
        toast.error(detail);
      }
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim() || code.length !== 6) { toast.error('Ingresa el código de 6 dígitos'); return; }
    setLoading(true);
    try {
      const res = await verifyRegistration(form.phone, code);
      login(res.data.user, res.data.token);
      toast.success('¡Cuenta verificada! Ya puedes acceder a los cofres.');
      navigate('/');
    } catch (err) { toast.error(err.response?.data?.detail || 'Código incorrecto'); }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginPhone.trim() || loginPhone.length < 10) { toast.error('Ingresa un número válido'); return; }
    if (!loginPassword.trim()) { toast.error('Ingresa tu contraseña'); return; }
    setLoading(true);
    try {
      const res = await userLogin(loginPhone, loginPassword);
      if (res.data.success) {
        login(res.data.user, res.data.token);
        toast.success('¡Bienvenido de nuevo!');
        navigate('/');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error';
      if (detail.includes('No hay cuenta')) {
        toast.error(detail);
        setForm(f => ({ ...f, phone: loginPhone }));
        setStep('register');
      } else {
        toast.error(detail);
      }
    }
    setLoading(false);
  };

  const handleResend = async () => {
    const phone = form.phone || loginPhone;
    if (!phone) return;
    setLoading(true);
    setCode('');
    try {
      const res = await resendCode(phone);
      const match = res.data.message?.match(/Demo: (\d+)/);
      if (match) setDemoCode(match[1]);
      toast.success('Código reenviado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al reenviar'); }
    setLoading(false);
  };

  // ===== CHOICE SCREEN =====
  if (step === 'choice') {
    return (
      <div className="relative z-10 min-h-[70vh] flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md bg-[#0E1016] border border-white/[0.08]">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-2xl bg-[rgba(255,198,64,0.1)] border border-[rgba(255,198,64,0.25)] flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-[#FFC640]" />
            </div>
            <CardTitle className="text-xl font-heading text-[#F7F2E7]">Acceso a Cofres Mágicos</CardTitle>
            <p className="text-sm text-[#F7F2E7]/50 mt-2">Para abrir cofres necesitas una cuenta verificada.</p>
            {referralInfo && (
              <div className="mt-3 p-3 bg-[rgba(53,208,127,0.08)] border border-[rgba(53,208,127,0.2)] rounded-xl">
                <p className="text-xs text-[#35D07F] flex items-center gap-1"><Info size={14} /> Fuiste invitado por un usuario de Cofres Mágicos.</p>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={() => setStep('register')}
              className="w-full h-12 bg-[#FFC640] text-[#090A0D] font-heading font-semibold hover:bg-[#FFD36B] rounded-xl text-base"
              data-testid="auth-register-button">
              <UserPlus size={18} className="mr-2" /> Registrarme
            </Button>
            <Button onClick={() => setStep('login')} variant="outline"
              className="w-full h-12 bg-[#121521] border-white/[0.12] text-[#F7F2E7] hover:border-[rgba(255,198,64,0.35)] rounded-xl text-base"
              data-testid="auth-login-button">
              <LogIn size={18} className="mr-2" /> Ya tengo cuenta
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== LOGIN SCREEN (phone + password) =====
  if (step === 'login') {
    return (
      <div className="relative z-10 min-h-[70vh] flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md bg-[#0E1016] border border-white/[0.08]">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-2xl bg-[rgba(255,198,64,0.1)] border border-[rgba(255,198,64,0.25)] flex items-center justify-center mx-auto mb-4">
              <LogIn size={28} className="text-[#FFC640]" />
            </div>
            <CardTitle className="text-xl font-heading text-[#F7F2E7]">Iniciar Sesión</CardTitle>
            <p className="text-sm text-[#F7F2E7]/50">Ingresa tu teléfono y contraseña</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#F7F2E7]/80 text-sm">Teléfono</Label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40" />
                  <Input value={loginPhone} onChange={(e) => setLoginPhone(e.target.value)}
                    className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] pl-10 focus:border-[#FFC640]"
                    placeholder="Ej: 5512345678" data-testid="login-phone-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#F7F2E7]/80 text-sm">Contraseña</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40" />
                  <Input value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                    type={showLoginPassword ? 'text' : 'password'}
                    className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] pl-10 pr-10 focus:border-[#FFC640]"
                    placeholder="Tu contraseña" data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40 hover:text-[#FFC640]"
                    data-testid="login-toggle-password">
                    {showLoginPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-[#FFC640] text-[#090A0D] font-heading font-semibold hover:bg-[#FFD36B] rounded-xl"
                data-testid="login-submit-button">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <>Entrar <ChevronRight size={16} className="ml-1" /></>}
              </Button>
              <button type="button" onClick={() => setStep('choice')}
                className="w-full text-sm text-[#F7F2E7]/50 hover:text-[#FFC640] transition-colors">Volver</button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== VERIFY REGISTRATION CODE =====
  if (step === 'verify') {
    return (
      <div className="relative z-10 min-h-[70vh] flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md bg-[#0E1016] border border-white/[0.08]">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-2xl bg-[rgba(255,198,64,0.1)] border border-[rgba(255,198,64,0.25)] flex items-center justify-center mx-auto mb-4">
              <KeyRound size={28} className="text-[#FFC640]" />
            </div>
            <CardTitle className="text-xl font-heading text-[#F7F2E7]">Verificar Teléfono</CardTitle>
            <p className="text-sm text-[#F7F2E7]/50">Ingresa el código de 6 dígitos enviado a</p>
            <p className="text-sm text-[#FFC640] font-medium">{formatPhoneDisplay(form.phone)}</p>
            {demoCode && (
              <div className="mt-2 p-2 bg-[rgba(255,198,64,0.1)] border border-[rgba(255,198,64,0.2)] rounded-lg">
                <p className="text-xs text-[#FFC640]">Demo: <span className="font-mono font-bold">{demoCode}</span></p>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] text-center text-xl tracking-[0.3em] focus:border-[#FFC640]"
                  placeholder="000000" maxLength={6} data-testid="verify-code-input"
                  inputMode="numeric" autoComplete="one-time-code" />
              </div>
              <Button type="submit" disabled={loading || code.length !== 6}
                className="w-full h-11 bg-[#FFC640] text-[#090A0D] font-heading font-semibold hover:bg-[#FFD36B] rounded-xl disabled:opacity-50"
                data-testid="verify-submit-button">
                {loading ? <Loader2 size={18} className="animate-spin" /> : 'Verificar mi cuenta'}
              </Button>
              <button type="button" onClick={handleResend} disabled={loading}
                className="w-full text-sm text-[#FFC640]/70 hover:text-[#FFC640] transition-colors flex items-center justify-center gap-1 disabled:opacity-50">
                <RefreshCw size={12} /> Reenviar código
              </button>
              <button type="button" onClick={() => { setStep('register'); setCode(''); setDemoCode(''); }}
                className="w-full text-sm text-[#F7F2E7]/50 hover:text-[#FFC640] transition-colors">Corregir datos</button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== REGISTRATION FORM =====
  return (
    <div className="relative z-10 min-h-[70vh] flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md bg-[#0E1016] border border-white/[0.08]">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-2xl bg-[rgba(255,198,64,0.1)] border border-[rgba(255,198,64,0.25)] flex items-center justify-center mx-auto mb-4">
            <UserPlus size={28} className="text-[#FFC640]" />
          </div>
          <CardTitle className="text-xl font-heading text-[#F7F2E7]">Registro</CardTitle>
          <p className="text-sm text-[#F7F2E7]/50">Completa tus datos para acceder a los cofres</p>
          {referralInfo && (
            <div className="mt-3 p-3 bg-[rgba(53,208,127,0.08)] border border-[rgba(53,208,127,0.2)] rounded-xl">
              <p className="text-xs text-[#35D07F] flex items-center gap-1"><Info size={14} /> Invitado por {referralInfo.referrer_name}</p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-3">
            {/* Name */}
            <div className="space-y-1">
              <Label className="text-[#F7F2E7]/80 text-xs">Nombre completo *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] focus:border-[#FFC640] h-10"
                placeholder="Tu nombre completo" data-testid="register-name-input" />
              {errors.name && <p className="text-xs text-red-400">{errors.name}</p>}
            </div>
            {/* Phone */}
            <div className="space-y-1">
              <Label className="text-[#F7F2E7]/80 text-xs">Teléfono (WhatsApp) *</Label>
              <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] focus:border-[#FFC640] h-10"
                placeholder="Ej: 5512345678" data-testid="register-phone-input" />
              {errors.phone && <p className="text-xs text-red-400">{errors.phone}</p>}
            </div>
            {/* Email */}
            <div className="space-y-1">
              <Label className="text-[#F7F2E7]/80 text-xs">Correo electrónico *</Label>
              <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] focus:border-[#FFC640] h-10"
                placeholder="tu@correo.com" data-testid="register-email-input" type="email" />
              {errors.email && <p className="text-xs text-red-400">{errors.email}</p>}
            </div>
            {/* State + Municipality dropdowns */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[#F7F2E7]/80 text-xs flex items-center gap-1"><MapPin size={12} /> Estado *</Label>
                <SearchableSelect
                  value={form.state}
                  onSelect={(val) => setForm({...form, state: val, municipality: ''})}
                  options={states}
                  placeholder="Selecciona estado"
                  disabled={false}
                  testId="register-state-select"
                />
                {errors.state && <p className="text-xs text-red-400">{errors.state}</p>}
              </div>
              <div className="space-y-1">
                <Label className="text-[#F7F2E7]/80 text-xs flex items-center gap-1"><MapPin size={12} /> Municipio *</Label>
                <SearchableSelect
                  value={form.municipality}
                  onSelect={(val) => setForm({...form, municipality: val})}
                  options={municipalities}
                  placeholder={form.state ? "Selecciona municipio" : "Primero elige estado"}
                  disabled={!form.state}
                  testId="register-municipality-select"
                />
                {errors.municipality && <p className="text-xs text-red-400">{errors.municipality}</p>}
              </div>
            </div>
            {/* Password */}
            <div className="space-y-1">
              <Label className="text-[#F7F2E7]/80 text-xs">Contraseña *</Label>
              <div className="relative">
                <Input value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                  type={showPassword ? 'text' : 'password'}
                  className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] focus:border-[#FFC640] h-10 pr-10"
                  placeholder="Mínimo 6 caracteres" data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40 hover:text-[#FFC640]"
                  data-testid="register-toggle-password">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-400">{errors.password}</p>}
            </div>
            {/* Confirm Password */}
            <div className="space-y-1">
              <Label className="text-[#F7F2E7]/80 text-xs">Confirmar contraseña *</Label>
              <div className="relative">
                <Input value={form.confirmPassword} onChange={(e) => setForm({...form, confirmPassword: e.target.value})}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] focus:border-[#FFC640] h-10 pr-10"
                  placeholder="Repite tu contraseña" data-testid="register-confirm-password-input" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40 hover:text-[#FFC640]"
                  data-testid="register-toggle-confirm-password">
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-400">{errors.confirmPassword}</p>}
            </div>
            {/* Referral code */}
            <div className="space-y-1">
              <Label className="text-[#F7F2E7]/80 text-xs">Código de referido (opcional)</Label>
              <Input value={form.referral_code}
                onChange={(e) => !referralLocked && setForm({...form, referral_code: e.target.value})}
                readOnly={referralLocked}
                className={`bg-[#121521] border-white/[0.12] text-[#F7F2E7] focus:border-[#FFC640] h-10 ${referralLocked ? 'opacity-70 cursor-not-allowed' : ''}`}
                placeholder="Ej: CM-XXXXX" data-testid="register-referral-input" />
              {referralLocked && <p className="text-xs text-[#35D07F]">Código de invitación aplicado</p>}
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-11 bg-[#FFC640] text-[#090A0D] font-heading font-semibold hover:bg-[#FFD36B] rounded-xl mt-2"
              data-testid="register-submit-button">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>Continuar <ChevronRight size={16} className="ml-1" /></>}
            </Button>
            <button type="button" onClick={() => setStep('choice')}
              className="w-full text-sm text-[#F7F2E7]/50 hover:text-[#FFC640] transition-colors">Volver</button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthGate;
